import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import moment from "moment";
import Swal from "sweetalert2";
import PageShell from "components/bento/PageShell";
import BentoCard from "components/bento/BentoCard";
import StyledDataGrid from "components/bento/StyledDataGrid";
import DataGridCustomToolbar from "components/DataGridCustomToolbar";
import { GetBulkMoveList, GetBulkMoveRouteGroups, BulkMoveToStation } from "api/packageBulkMove";
import { GetCustomersList } from "api/customers";
import { GetAllStationsAdmin } from "api/stations";
import { GetRouteList } from "api/routes";
import { playAlertSound, playCorrectSound } from "utils";

const PackageBulkMove = () => {
  const { t } = useTranslation();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isMoving, setIsMoving] = useState(false);

  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [selectionModel, setSelectionModel] = useState([]);

  const [stationList, setStationList] = useState([]);
  const [routeList, setRouteList] = useState([]);

  // Filters: customer, current station, route, status, updated-at date range
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchInput, setCustomerSearchInput] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [selectedStationId, setSelectedStationId] = useState("");
  const [selectedRouteId, setSelectedRouteId] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [fromDate, setFromDate] = useState(null);
  const [toDate, setToDate] = useState(null);

  // Move dialog — the selection is split into route groups (fetched from the
  // server, since the selection can span pages that aren't loaded), and each
  // group picks its own target from the stations on its route.
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [isLoadingGroups, setIsLoadingGroups] = useState(false);
  const [routeGroups, setRouteGroups] = useState([]);
  const [groupTargets, setGroupTargets] = useState({});
  const [preSkippedCount, setPreSkippedCount] = useState(0);
  const [moveRemark, setMoveRemark] = useState("");
  const MIN_REMARK_LENGTH = 10;
  const isRemarkValid = moveRemark.trim().length >= MIN_REMARK_LENGTH;
  const movableGroups = routeGroups.filter((g) => g.stations.length > 0);
  const allGroupsTargeted = movableGroups.length > 0 && movableGroups.every((g) => groupTargets[g.routeId]);

  const statusOptions = Array.from(
    new Set(["Pending", ...stationList.map((s) => s.parcelStatus).filter(Boolean)])
  );

  const clearFilters = () => {
    setSelectedCustomer(null);
    setCustomerSearchInput("");
    setSelectedStationId("");
    setSelectedRouteId("");
    setSelectedStatus("");
    setFromDate(null);
    setToDate(null);
  };

  // Nothing is listed until the user searches/scans or applies a filter.
  const hasQuery = !!(
    search || selectedCustomer || selectedStationId || selectedRouteId || selectedStatus || fromDate || toDate
  );

  const fetchList = () => {
    if (!hasQuery) {
      setRows([]);
      setTotalCount(0);
      return;
    }
    setIsLoading(true);
    const filters = {
      page,
      pageSize,
      search,
      customerId: selectedCustomer ? selectedCustomer._id : undefined,
      station: selectedStationId || undefined,
      routeId: selectedRouteId || undefined,
      status: selectedStatus || undefined,
      fromDate: fromDate ? moment(fromDate).format("YYYY-MM-DD") : undefined,
      toDate: toDate ? moment(toDate).format("YYYY-MM-DD") : undefined,
    };
    GetBulkMoveList(filters).then((res) => {
      if (res.success) {
        setRows(res.data);
        setTotalCount(res.total);
      } else {
        console.error(res.message);
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    // Every station, including STD/END, for the current-station filter. Move
    // targets come per route group from GetBulkMoveRouteGroups instead.
    GetAllStationsAdmin().then((res) => {
      if (res && res.success && Array.isArray(res.data)) setStationList(res.data);
    });
    GetRouteList().then((res) => {
      if (res && res.success && Array.isArray(res.data)) setRouteList(res.data);
    });
  }, []);

  useEffect(() => {
    GetCustomersList(customerSearchInput).then((res) => {
      setCustomerOptions(res.success && res.data ? res.data : []);
    });
  }, [customerSearchInput]);

  const filterDeps = [
    selectedCustomer ? selectedCustomer._id : "",
    selectedStationId,
    selectedRouteId,
    selectedStatus,
    fromDate,
    toDate,
  ];

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, search, ...filterDeps]);

  useEffect(() => {
    setPage(0);
    setSelectionModel([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, ...filterDeps]);

  const closeMoveDialog = () => {
    setMoveDialogOpen(false);
    setMoveRemark("");
  };

  const openMoveDialog = () => {
    setIsLoadingGroups(true);
    GetBulkMoveRouteGroups(selectionModel).then((res) => {
      setIsLoadingGroups(false);
      if (!res.success) {
        playAlertSound();
        Swal.fire({ icon: "error", title: t("packageBulkMove.loadGroupsError"), text: res.message });
        return;
      }
      const groups = res.groups || [];
      setRouteGroups(groups);
      setGroupTargets({});
      // Not found/cancelled, plus groups with no valid route — never sent to /move.
      setPreSkippedCount(
        (res.skipped?.length || 0) +
          groups.filter((g) => g.stations.length === 0).reduce((n, g) => n + g.packageIds.length, 0)
      );
      setMoveRemark("");
      setMoveDialogOpen(true);
    });
  };

  const handleConfirmMove = () => {
    if (!allGroupsTargeted || !isRemarkValid) return;
    const moves = movableGroups.map((g) => ({ targetStationId: groupTargets[g.routeId], packageIds: g.packageIds }));
    setIsMoving(true);
    BulkMoveToStation(moves, moveRemark.trim()).then((res) => {
      setIsMoving(false);
      if (res.success) {
        playCorrectSound();
        closeMoveDialog();
        setSelectionModel([]);
        const skippedCount = preSkippedCount + (res.skipped?.length || 0);
        Swal.fire({
          icon: "success",
          title: t("packageBulkMove.moveSuccessTitle"),
          text: (res.targetStation
            ? t("packageBulkMove.moveSuccessBody", { count: res.movedCount, station: res.targetStation })
            : t("packageBulkMove.moveSuccessMulti", { count: res.movedCount })
          ) + (skippedCount > 0 ? ` ${t("packageBulkMove.moveSkipped", { count: skippedCount })}` : ""),
        });
        fetchList();
      } else {
        playAlertSound();
        Swal.fire({ icon: "error", title: t("packageBulkMove.moveErrorTitle"), text: res.message });
      }
    });
  };

  const columns = [
    { field: "tracking", headerName: t("packageBulkMove.columnTracking"), flex: 1 },
    {
      field: "customerName",
      headerName: t("packageBulkMove.columnCustomer"),
      flex: 1,
      valueGetter: (params) => params.row.customer?.name || "",
    },
    { field: "description", headerName: t("packageBulkMove.columnDescription"), flex: 1.5 },
    { field: "station", headerName: t("packageBulkMove.columnStation"), flex: 0.5 },
    { field: "status", headerName: t("packageBulkMove.columnStatus"), flex: 0.5 },
    { field: "routeId", headerName: t("packageBulkMove.columnRoute"), flex: 0.5 },
    {
      field: "updatedAt",
      headerName: t("packageBulkMove.columnUpdatedAt"),
      flex: 0.8,
      valueGetter: (params) =>
        params.row.updatedAt
          ? moment(params.row.updatedAt).tz("Asia/Vientiane").format("DD/MM/YYYY, h:mm:ss A")
          : "",
    },
  ];

  return (
    <PageShell title={t("packageBulkMove.title")} subtitle={t("packageBulkMove.subtitle")}>
      <BentoCard sx={{ mb: "16px" }} bodySx={{ p: "20px 22px" }}>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr", xl: "repeat(6, 1fr)" },
            gap: "16px",
            alignItems: "start",
          }}
        >
          <Autocomplete
            fullWidth
            options={customerOptions}
            value={selectedCustomer}
            onInputChange={(event, newInputValue) => setCustomerSearchInput(newInputValue)}
            onChange={(event, value) => setSelectedCustomer(value)}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            getOptionLabel={(option) => {
              if (!option || !option.name) return "";
              return option.name + (option.whatsapp ? ` - ${option.whatsapp}` : "");
            }}
            renderInput={(params) => <TextField {...params} label={t("packageBulkMove.filterCustomer")} />}
          />
          <FormControl fullWidth>
            <InputLabel>{t("packageBulkMove.filterStation")}</InputLabel>
            <Select
              label={t("packageBulkMove.filterStation")}
              value={selectedStationId}
              onChange={(e) => setSelectedStationId(e.target.value)}
            >
              <MenuItem value="">{t("packageBulkMove.filterAll")}</MenuItem>
              {stationList.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {id} ({name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t("packageBulkMove.filterRoute")}</InputLabel>
            <Select
              label={t("packageBulkMove.filterRoute")}
              value={selectedRouteId}
              onChange={(e) => setSelectedRouteId(e.target.value)}
            >
              <MenuItem value="">{t("packageBulkMove.filterAll")}</MenuItem>
              {routeList.map(({ id, name }) => (
                <MenuItem key={id} value={id}>
                  {id} ({name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>{t("packageBulkMove.filterStatus")}</InputLabel>
            <Select
              label={t("packageBulkMove.filterStatus")}
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
            >
              <MenuItem value="">{t("packageBulkMove.filterAll")}</MenuItem>
              {statusOptions.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
              label={t("packageBulkMove.filterFromDate")}
              inputFormat="DD/MM/yyyy"
              value={fromDate}
              maxDate={toDate || undefined}
              onChange={(newValue) => setFromDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DesktopDatePicker
              label={t("packageBulkMove.filterToDate")}
              inputFormat="DD/MM/yyyy"
              value={toDate}
              minDate={fromDate || undefined}
              onChange={(newValue) => setToDate(newValue)}
              renderInput={(params) => <TextField {...params} fullWidth />}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </LocalizationProvider>
        </Box>
        <Box sx={{ mt: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="body2" color="text.secondary">
            {t("packageBulkMove.selectedCount", { count: selectionModel.length })}
          </Typography>
          <Box sx={{ display: "flex", gap: "12px" }}>
            <Button variant="outlined" color="secondary" onClick={clearFilters}>
              {t("packageBulkMove.clearFilters")}
            </Button>
            <Button
              variant="contained"
              color="secondary"
              disabled={selectionModel.length === 0 || isLoadingGroups}
              onClick={openMoveDialog}
            >
              {t("packageBulkMove.moveButton", { count: selectionModel.length })}
            </Button>
          </Box>
        </Box>
      </BentoCard>

      <Box height="70vh" width="100%">
        <StyledDataGrid
          loading={isLoading}
          getRowId={(row) => row._id}
          rows={rows || []}
          columns={columns}
          checkboxSelection
          selectionModel={selectionModel}
          onSelectionModelChange={(model) => setSelectionModel(model)}
          rowCount={totalCount || 0}
          // @mui/x-data-grid Community hard-caps pageSize at 100 (Pro/Premium-only above that)
          rowsPerPageOptions={[25, 50, 100]}
          pagination
          page={page}
          pageSize={pageSize}
          paginationMode="server"
          onPageChange={(newPage) => setPage(newPage)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          localeText={{ noRowsLabel: t(hasQuery ? "packageBulkMove.noPackages" : "packageBulkMove.searchFirst") }}
          components={{ Toolbar: DataGridCustomToolbar }}
          componentsProps={{ toolbar: { searchInput, setSearchInput, setSearch } }}
          containerSx={{ height: "100%" }}
          autoHeight={false}
        />
      </Box>

      <Dialog
        open={moveDialogOpen}
        onClose={closeMoveDialog}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{t("packageBulkMove.dialogTitle", { count: selectionModel.length })}</DialogTitle>
        <DialogContent>
          {routeGroups.map((group) => (
            <Box key={group.routeId} sx={{ mt: "8px", mb: "8px" }}>
              <Typography variant="subtitle2" color="secondary" sx={{ mb: "8px" }}>
                {t("packageBulkMove.dialogRouteGroup", {
                  route: group.routeId
                    ? `${group.routeId}${group.routeName ? ` (${group.routeName})` : ""}`
                    : t("packageBulkMove.dialogNoRoute"),
                  count: group.packageIds.length,
                })}
              </Typography>
              {group.stations.length === 0 ? (
                <Alert severity="info">{t("packageBulkMove.dialogGroupNoStations")}</Alert>
              ) : (
                <FormControl fullWidth>
                  <InputLabel>{t("packageBulkMove.dialogTargetStation")}</InputLabel>
                  <Select
                    label={t("packageBulkMove.dialogTargetStation")}
                    value={groupTargets[group.routeId] || ""}
                    onChange={(e) => setGroupTargets((prev) => ({ ...prev, [group.routeId]: e.target.value }))}
                  >
                    {group.stations.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {id} ({name})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            </Box>
          ))}
          <TextField
            fullWidth
            multiline
            minRows={3}
            sx={{ mt: "16px" }}
            label={t("packageBulkMove.dialogRemarkLabel")}
            placeholder={t("packageBulkMove.dialogRemarkPlaceholder")}
            value={moveRemark}
            onChange={(e) => setMoveRemark(e.target.value)}
            error={moveRemark.length > 0 && !isRemarkValid}
            helperText={t("packageBulkMove.dialogRemarkHelper", { count: moveRemark.trim().length, min: MIN_REMARK_LENGTH })}
          />
          <Alert severity="warning" sx={{ mt: "16px" }}>
            {t("packageBulkMove.dialogWarning")}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" color="secondary" onClick={closeMoveDialog}>
            {t("common.cancel")}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            disabled={!allGroupsTargeted || !isRemarkValid || isMoving}
            onClick={handleConfirmMove}
          >
            {t("packageBulkMove.dialogConfirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </PageShell>
  );
};

export default PackageBulkMove;
